import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useTagContext } from '../contexts/TagContext'
import {
  getRelatedContractorName,
  updateAdditionalExpenses,
} from '../services/tagService'
import type { Tag, TagStatus } from '../types/tag'
import AdditionalExpensesForm from './AdditionalExpensesForm'
import BackButton from './BackButton'
import CumulativeLoadInfo from './CumulativeLoadInfo'
import DurationSelector from './DurationSelector'
import OpenMap from './OpenMaps'
import PhoneContact from './PhoneContact'
import StatusBadge from './StatusBadge'
import TruckInfo from './TruckInfo'
import CardRow from './card/CardRow'

export interface TagHeaderProps {
  tagId?: string
  title: string
  status: TagStatus
  backUrl?: string
  initialIsExpanded?: boolean
  isDetailed?: boolean
  backButton?: boolean
  toggleTags?: boolean
  onBackClick?: () => void
  isSupervisorSignature?: boolean
  showAdditionalExpenses?: boolean
  onEstimatedTravelTimeChange?: (duration: string) => void
  estimatedTravelTime?: string
}

const TagHeader: React.FC<TagHeaderProps> = ({
  tagId,
  title,
  status,
  backUrl,
  initialIsExpanded = false,
  isDetailed = false,
  backButton = true,
  toggleTags = true,
  onBackClick,
  isSupervisorSignature = false,
  showAdditionalExpenses = false,
  onEstimatedTravelTimeChange = () => {},
  estimatedTravelTime = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(initialIsExpanded)
  const [selectedDuration, setSelectedDuration] = useState(estimatedTravelTime)
  const [refreshKey, setRefreshKey] = useState(0)
  const { getCurrentTag, refreshTag } = useTagContext()
  const tag = getCurrentTag(tagId) as Tag | undefined
  const [contractorName, setContractorName] = useState<string>('-')

  useEffect(() => {
    setIsExpanded(initialIsExpanded)
    setSelectedDuration(estimatedTravelTime)
  }, [initialIsExpanded, estimatedTravelTime])

  useEffect(() => {
    const fetchContractorName = async () => {
      if (tag) {
        const name = await getRelatedContractorName(tag)
        setContractorName(name)
      }
    }
    fetchContractorName()
  }, [tag])

  const renderTagContent = tag && isExpanded

  const handleTravelTimeChange = async (duration: string) => {
    if (tagId) {
      try {
        setSelectedDuration(duration)
        await updateAdditionalExpenses(tagId, {
          tolls: '',
          notes: '',
          yardTime: '',
          homeTime: duration.replace(/[MH]/, ''),
        })
        onEstimatedTravelTimeChange(duration)
      } catch (error) {
        setSelectedDuration(estimatedTravelTime)
        console.error('Failed to update estimated travel time:', error)
      }
    }
  }

  const handleAdditionalExpensesUpdate = async () => {
    if (tagId) {
      // Refresh the tag data
      await refreshTag(tagId)
      // Force refresh of cumulative data
      setRefreshKey((prev) => prev + 1)
    }
  }

  return (
    <div className={css(styles.headerSection)}>
      <div className={css(styles.mainContent)}>
        <div className={css(styles.topRow)}>
          {backButton && (
            <BackButton backUrl={backUrl} onBackClick={onBackClick} />
          )}
          <h1 className={css(styles.title)}>{title}</h1>
          <StatusBadge status={status} />
        </div>

        {renderTagContent && (
          <>
            <div className={css(styles.tagInfo)}>
              <div className={css(styles.tagIdContainer)}>
                <span className={css(styles.tagId)}>Tag #: {tag.label}</span>
              </div>
              {isDetailed && (
                <div className={css(styles.locationContainer)}>
                  <CardRow
                    label="PICK UP LOCATION"
                    value={
                      <OpenMap
                        address={tag.origin_address || ''}
                        className={css(styles.locationText)}
                      />
                    }
                    vertical={true}
                  />
                  <CardRow
                    label="DROP OFF LOCATION"
                    value={
                      <OpenMap
                        address={tag.destination_address || ''}
                        className={css(styles.locationText)}
                      />
                    }
                    vertical={true}
                  />
                </div>
              )}
            </div>

            <div
              className={css(
                styles.expandedContent,
                !isDetailed && styles.expandedContentHorizontal,
              )}
            >
              {/* Row 1 */}
              <CardRow
                label="START DATE"
                value={new Date(tag.created_date).toLocaleDateString()}
                vertical={isDetailed}
              />
              <CardRow
                label="START TIME"
                value={new Date(tag.created_date).toLocaleTimeString()}
                vertical={isDetailed}
              />
              <CardRow
                label="DISPATCHER"
                value={
                  <div
                    style={{
                      maxWidth: 140,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: 24,
                    }}
                  >
                    <PhoneContact
                      phoneNumber={tag.created_by.phone_number}
                      displayName={`${tag.created_by.first_name} ${tag.created_by.last_name}`.trim()}
                    />
                  </div>
                }
                vertical={isDetailed}
              />

              {/* Row 2 */}
              <CardRow
                label="CONTRACTOR"
                value={contractorName}
                vertical={isDetailed}
              />
              {isDetailed && (
                <>
                  <TruckInfo />
                  <CumulativeLoadInfo key={refreshKey} tag={tag} />
                </>
              )}
            </div>
            {showAdditionalExpenses && tagId && (
              <>
                <div className={css(styles.divider)} />
                <AdditionalExpensesForm
                  tagId={tagId}
                  tag={tag}
                  onUpdate={handleAdditionalExpensesUpdate}
                />
              </>
            )}
            {isSupervisorSignature && (
              <div className={css(styles.travelTimeSection)}>
                <CardRow
                  label="TRAVEL TIME"
                  value={
                    <DurationSelector
                      value={selectedDuration}
                      onChange={handleTravelTimeChange}
                      durations={['15M', '30M', '45M', '1H', '2H']}
                    />
                  }
                  vertical={true}
                />
              </div>
            )}
          </>
        )}
      </div>

      {toggleTags && (
        <div className={css(styles.toggleContainer)}>
          <button
            className={css(styles.expandButton)}
            onClick={() => setIsExpanded(!isExpanded)}
            onTouchEnd={(e) => {
              e.preventDefault()
              setIsExpanded(!isExpanded)
            }}
          >
            <i
              className={`fa-solid ${
                isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'
              }`}
            />
          </button>
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  headerSection: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    transition: 'all 0.3s ease-in-out',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  mainContent: {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: '8px',
  },
  toggleContainer: {
    width: '100%',
    bottom: 0,
    left: 0,
    display: 'flex',
    paddingBottom: 8,
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    zIndex: 1,
  },
  expandButton: {
    display: 'flex',
    flexDirection: 'column',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6B7280',
    padding: '12px',
    minWidth: '44px',
    minHeight: '44px',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    ':hover': {
      color: '#4B5563',
    },
    ':active': {
      color: '#374151',
    },
  },
  tagLabel: {
    marginBottom: 4,
    fontSize: 12,
    marginTop: 12,
  },
  tagInfo: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  tagIdContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  tagId: {
    color: '#046C4E',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '150%',
    fontFamily: 'Inter',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#111928',
    lineHeight: '150%',
    fontFamily: 'Inter',
    flex: 1,
    marginBottom: 0,
  },
  expandedContent: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)',
    gap: '16px',
    width: '100%',
    marginTop: '8px',
    borderTop: '1px solid #E5E7EB',
    paddingTop: '16px',
  },
  expandedContentHorizontal: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  locationText: {
    color: '#1C64F2',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '150%',
    fontFamily: 'Inter',
  },
  locationContainer: {
    display: 'flex',
    gap: '16px',
    marginTop: '8px',
    width: '100%',
  },
  travelTimeSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTop: '1px solid #E5E7EB',
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: '#E5E7EB',
    margin: '16px 0',
  },
})

export default TagHeader
